/**
 * portalOnizleme.js — "Portalı bu firma olarak önizle" (backlog #411, 2026-10-13; tasarım #409 D11).
 *
 * Personel (page.firma yetkisi olan herhangi bir personel girişi), Firma Detay'dan bir firmanın portalını SEÇİLİ FUARDA
 * salt okunur açar: o firmanın (ve isteğe bağlı bir setinin) menüsü, sayfaları ve verisi — ama hiçbir şey kaydedilmez.
 * Backend personele yazma izni verir (personel MANAGE'dir); önizlemenin yazmaması bu dosyanın işi:
 *
 *   1. Önizleme durumu sessionStorage'ta (`portalOnizleme`): sekmeye özel, sekme kapanınca biter.
 *   2. Her axios isteği, önizleme açıkken GET / HEAD / OPTIONS dışında İSTEK GİTMEDEN reddedilir (ağ günlüğünde yazma
 *      isteği görünmez). Tek istisna: personelin kendi aktif fuarını değiştirmesi (PUT /users/updateUsersActiveFuar),
 *      çünkü önizleme seçili fuarı kullanır ve başlıktaki fuar seçici çalışmalı; o firma verisine yazmaz.
 *   3. PortalContext aynı zamanda her modül seviyesini en çok READ'e indirir ve organizatör eylemlerini kapatır, yani
 *      yazma düğmeleri zaten görünmez / pasiftir; 2. madde arkadaki güvence.
 *
 * Önizleme bir oturum değildir: personelin kendi girişi değişmez, firma olarak giriş yapılmaz.
 */
import { useMemo, useSyncExternalStore } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const ANAHTAR = 'portalOnizleme';
const OLAY = 'portalOnizlemeDegisti';
const OKUMA_YONTEMLERI = new Set(['get', 'head', 'options']);
// Önizlemede de gitmesine izin verilen tek yazma: personelin KENDİ aktif fuarı (başlıktaki fuar seçici).
// #562: ... and their own interface language (PUT /users/me/language, the header's TR / EN switch): it is written on the STAFF login
// (the session's user), never on the previewed company, so the switch has to keep working while a company is previewed.
// WAS (until #562): const IZINLI_YAZMALAR = [/\/users\/updateUsersActiveFuar$/];
const IZINLI_YAZMALAR = [/\/users\/updateUsersActiveFuar$/, /\/users\/me\/language$/];

function oku() {
  try {
    return sessionStorage.getItem(ANAHTAR);
  } catch {
    return null;
  }
}

function coz(ham) {
  if (!ham) return null;
  try {
    const v = JSON.parse(ham);
    const firmaId = Number(v?.firmaId);
    if (!Number.isFinite(firmaId) || firmaId <= 0) return null;
    // #417: kullaniciId = "portalı bu kullanıcı olarak önizle" (o girişin rolleriyle); yoksa firma, setlerinin tavanında.
    const kullaniciId = Number(v?.kullaniciId);
    // WAS (until #739): the object had no yeniSekme field.
    return {
      firmaId, firmaAdi: v.firmaAdi || '', arayuz: v.arayuz || null,
      kullaniciId: Number.isFinite(kullaniciId) && kullaniciId > 0 ? kullaniciId : null, kullaniciAdi: v.kullaniciAdi || '',
      yeniSekme: v.yeniSekme === true,
    };
  } catch {
    return null;
  }
}

function duyur() {
  try {
    window.dispatchEvent(new Event(OLAY));
  } catch {
    /* sunucu tarafı / test ortamı */
  }
}

/** { firmaId, firmaAdi, arayuz, kullaniciId, kullaniciAdi } ya da null. */
export function getOnizleme() {
  return coz(oku());
}

/** Önizlemeyi başlatır; arayuz (örn. YABANCI_KATILIMCI) verilirse o set açık gelir; kullaniciId verilirse o girişin rolleriyle (#417). */
// WAS (until #417): export function startOnizleme({ firmaId, firmaAdi, arayuz } = {}) { ... JSON.stringify({ firmaId, firmaAdi, arayuz }) ... }
// #739: yeniSekme = this tab was opened just for the preview (openOnizlemeYeniSekme), so "Önizlemeyi kapat" closes the tab.
export function startOnizleme({ firmaId, firmaAdi, arayuz, kullaniciId, kullaniciAdi, yeniSekme } = {}) {
  try {
    sessionStorage.setItem(ANAHTAR, JSON.stringify({
      firmaId, firmaAdi: firmaAdi || '', arayuz: arayuz || null, kullaniciId: kullaniciId || null, kullaniciAdi: kullaniciAdi || '',
      yeniSekme: yeniSekme === true,
    }));
  } catch {
    /* sessionStorage kapalıysa önizleme açılamaz: hiçbir şey olmaz */
  }
  duyur();
}

// ---- #739: the preview opens in a NEW TAB ----------------------------------------------------------------------------
// The flag lives in sessionStorage, which a new tab does not share, and setting it in the staff tab would make THAT tab
// read-only (the interceptor below blocks every write while it is set). So the preview travels in the URL: the staff tab
// stays untouched, the new tab reads the parameters once when the app loads, sets its own flag and cleans the address.
const ONIZLEME_YOLU = '/Firma/Anasayfa';
const ADRES_PARAMETRELERI = ['onizle', 'kullanici', 'firma', 'kad', 'arayuz'];

/** The address that starts a preview in a fresh tab: /Firma/Anasayfa?onizle=<firmaId>[&kullanici=<userId>&firma=..&kad=..]. */
export function onizlemeAdresi({ firmaId, firmaAdi, arayuz, kullaniciId, kullaniciAdi } = {}) {
  const q = new URLSearchParams();
  q.set('onizle', String(firmaId));
  if (kullaniciId) q.set('kullanici', String(kullaniciId));
  if (firmaAdi) q.set('firma', firmaAdi);
  if (kullaniciAdi) q.set('kad', kullaniciAdi);
  if (arayuz) q.set('arayuz', arayuz);
  return `${ONIZLEME_YOLU}?${q.toString()}`;
}

/** Opens the preview in a new tab (noopener: the new tab gets its own empty sessionStorage and no handle on this one). */
export function openOnizlemeYeniSekme(onizleme) {
  window.open(onizlemeAdresi(onizleme), '_blank', 'noopener');
}

/**
 * Called once when the app loads (below): when the address is /Firma/Anasayfa?onizle=.., starts the preview in THIS tab
 * (flagged yeniSekme). Returns true when a preview was started; adreseUygula then removes the parameters from the
 * address bar (a reload keeps the preview through sessionStorage).
 */
export function onizlemeyiAdrestenBaslat(yol, arama) {
  const q = new URLSearchParams(arama);
  const firmaId = Number(q.get('onizle'));
  if (yol !== ONIZLEME_YOLU || !Number.isFinite(firmaId) || firmaId <= 0) return false;
  const kullaniciId = Number(q.get('kullanici'));
  startOnizleme({
    firmaId, firmaAdi: q.get('firma') || '', arayuz: q.get('arayuz') || null,
    kullaniciId: Number.isFinite(kullaniciId) && kullaniciId > 0 ? kullaniciId : undefined, kullaniciAdi: q.get('kad') || '',
    yeniSekme: true,
  });
  return true;
}

function adreseUygula() {
  try {
    const { pathname, search, hash } = window.location;
    const q = new URLSearchParams(search);
    if (!onizlemeyiAdrestenBaslat(pathname, search)) return;
    ADRES_PARAMETRELERI.forEach((k) => q.delete(k));
    const kalan = q.toString();
    window.history.replaceState(window.history.state, '', `${pathname}${kalan ? `?${kalan}` : ''}${hash}`);
  } catch {
    /* sunucu tarafı / test ortamı */
  }
}

export function stopOnizleme() {
  try {
    sessionStorage.removeItem(ANAHTAR);
  } catch {
    /* yok say */
  }
  duyur();
}

function abone(geriCagri) {
  window.addEventListener(OLAY, geriCagri);
  window.addEventListener('storage', geriCagri);
  return () => {
    window.removeEventListener(OLAY, geriCagri);
    window.removeEventListener('storage', geriCagri);
  };
}

/** Önizleme açıkken { firmaId, firmaAdi, arayuz, kullaniciId, kullaniciAdi }, kapalıyken null; durum değişince bileşeni yeniler. */
export function useOnizleme() {
  const ham = useSyncExternalStore(abone, oku, () => null);
  return useMemo(() => coz(ham), [ham]);
}

/** Önizlemede bir HTTP yöntemi / adresi gitmeli mi? (Saf işlev: interceptor ve testler bunu kullanır.) */
export function onizlemedeGidebilir(yontem, url) {
  const m = String(yontem || 'get').toLowerCase();
  if (OKUMA_YONTEMLERI.has(m)) return true;
  const yol = String(url || '').split('?')[0];
  return IZINLI_YAZMALAR.some((re) => re.test(yol));
}

// Kayıt yeri tek: modül yüklenince bir kez. Önizleme kapalıyken tek maliyeti bir sessionStorage okuması.
axios.interceptors.request.use((config) => {
  if (!oku()) return config;
  if (onizlemedeGidebilir(config.method, config.url)) return config;
  const hata = new Error(i18n.t('components:portalOnizleme.yazmaKapali'));
  hata.onizlemeEngeli = true;
  hata.config = config;
  return Promise.reject(hata);
});

// #739: a tab opened by openOnizlemeYeniSekme starts its preview here, before the first render (the flag must be set by then).
adreseUygula();
